import { useState, useCallback, useMemo, useEffect } from 'react';

const DEFAULT_STATE = Object.freeze({
  searchQuery: '',
  sortBy: '',
  order: '',
  debouncedSearchQuery: ''
});


export const useSearchAndSort = (debounceDelay = 500, sectionKey = 'default') => {
  const [state, setState] = useState(DEFAULT_STATE);
  const [sectionStates, setSectionStates] = useState({});

  const currentSectionState = useMemo(() => 
    sectionStates[sectionKey] ?? DEFAULT_STATE, 
    [sectionStates, sectionKey]
  );

  const updateState = useCallback((updater) => {
    setState(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      setSectionStates(prevStates => ({ ...prevStates, [sectionKey]: newState }));
      return newState;
    });
  }, [sectionKey]);

  useEffect(() => {
    if (state === currentSectionState) return;
    setState(currentSectionState);
  }, [currentSectionState, state]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateState(prev => ({ ...prev, debouncedSearchQuery: prev.searchQuery }));
    }, debounceDelay);

    return () => clearTimeout(timeoutId);
  }, [state.searchQuery, debounceDelay, updateState]);

  const handleSort = useCallback((columnId, direction) => {
    updateState(prev => ({
      ...prev,
      sortBy: columnId,
      order: direction === 'asc' ? 'asc' : 'desc'
    }));
  }, [updateState]);

  const setSearchQuery = useCallback((value) => {
    updateState(prev => ({ ...prev, searchQuery: value }));
  }, [updateState]);

  const searchAndSortParams = useMemo(() => {
    const { debouncedSearchQuery, sortBy, order } = currentSectionState;
    const params = {
      searchQuery: debouncedSearchQuery || undefined,
      sortBy: sortBy || undefined,
      order: order || undefined,
    };
    return params;
  }, [currentSectionState]);

  return {
    ...state,
    setSearchQuery,
    handleSort,
    searchAndSortParams,
  };
};

export default useSearchAndSort;

